import { Button } from '@massilia/ui'
import { RefreshCw, Search } from 'lucide-react'

import type { ClientErrorType } from '@/lib/client-error'

import { ClientErrorState } from '@/components/errors/client-error-state'

interface FlightConditionsErrorProps {
  /** L'erreur d'origine, quand l'échec est venu du transport ou d'un statut HTTP. */
  error?: Error | null
  onClose: () => void
  onRestartSearch: () => void
  onRetry: () => void
  /**
   * Écart de tarif en centimes, quand le serveur l'a chiffré.
   *
   * C'est bien l'écart, et non les deux prix : le serveur compare des coûts vol
   * sec, hors marge, qui ne correspondent à aucun montant que le client a vu.
   * La variation, elle, est la même des deux côtés — la marge est un montant
   * fixe — donc elle se dit sans rien révéler du prix d'achat.
   */
  priceDriftCents?: number
  type: ClientErrorType
}

/**
 * L'échec de la sheet « Détails des billets » : le catalogue partagé pour le
 * texte, et les actions propres à cette surface.
 *
 * Un « Réessayer » n'a rien à faire sur un tarif qui a bougé ni sur une session
 * expirée — le même appel rendrait le même verdict, puisque le prix de référence
 * reste celui de la recherche d'origine. Là, la seule sortie est de relancer la
 * recherche.
 */
export function FlightConditionsError({
  error,
  onClose,
  onRestartSearch,
  onRetry,
  priceDriftCents,
  type,
}: FlightConditionsErrorProps) {
  const isRetryable = type !== 'price-changed' && type !== 'session-expired'

  return (
    <ClientErrorState
      actions={
        <>
          {isRetryable ? (
            <Button className="bg-brand-600 hover:bg-brand-700" onClick={onRetry}>
              <RefreshCw className="size-4" />
              Réessayer
            </Button>
          ) : (
            <Button className="bg-brand-600 hover:bg-brand-700" onClick={onRestartSearch}>
              <Search className="size-4" />
              Relancer la recherche
            </Button>
          )}
          <Button onClick={onClose} variant="ghost">
            Fermer
          </Button>
        </>
      }
      error={error}
      priceDriftCents={priceDriftCents}
      type={type}
    />
  )
}
